import {
  parseStoredDashboardTourSession,
  type DashboardTourSessionState,
} from "./schema";

const STORAGE_KEY_PREFIX = "staylayer:onboarding:dashboard:v1:";
const AUTO_STARTED_KEY_PREFIX = "staylayer:onboarding:dashboard:auto-started:";
const DISABLED_KEY = "staylayer:onboarding:dashboard:disabled";

function storageKeyFor(userId: string, tenantId: string) {
  return `${STORAGE_KEY_PREFIX}${userId}:${tenantId}`;
}

function autoStartedKeyFor(userId: string, tenantId: string) {
  return `${AUTO_STARTED_KEY_PREFIX}${userId}:${tenantId}`;
}

export function loadDashboardTourSession(
  userId: string,
  tenantId: string,
): DashboardTourSessionState | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId, tenantId));
    if (!raw) return null;
    return parseStoredDashboardTourSession(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function saveDashboardTourSession(
  state: DashboardTourSessionState,
): void {
  try {
    localStorage.setItem(
      storageKeyFor(state.userId, state.tenantId),
      JSON.stringify(state),
    );
  } catch {
    // ignore storage failures; the tour still works in memory
  }
}

export function clearDashboardTourSession(
  userId: string,
  tenantId: string,
): void {
  try {
    localStorage.removeItem(storageKeyFor(userId, tenantId));
  } catch {
    // ignore
  }
}

export function hasDashboardTourAutoStarted(
  userId: string,
  tenantId: string,
): boolean {
  try {
    return localStorage.getItem(autoStartedKeyFor(userId, tenantId)) === "1";
  } catch {
    return false;
  }
}

export function markDashboardTourAutoStarted(
  userId: string,
  tenantId: string,
): void {
  try {
    localStorage.setItem(autoStartedKeyFor(userId, tenantId), "1");
  } catch {
    // ignore storage failures; auto-start may repeat in this browser
  }
}

export function isDashboardTourDisabledLocally(): boolean {
  try {
    return localStorage.getItem(DISABLED_KEY) === "1";
  } catch {
    return false;
  }
}

export function createDashboardTourDebouncedSaver(delayMs = 250) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending: DashboardTourSessionState | null = null;

  const flush = () => {
    if (pending) {
      saveDashboardTourSession(pending);
      pending = null;
    }
    timer = null;
  };

  return {
    save(state: DashboardTourSessionState) {
      pending = state;
      if (timer) return;
      timer = setTimeout(flush, delayMs);
    },
    flushNow() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      flush();
    },
  };
}
